import { useState } from "react";
import { toast } from "react-hot-toast";
import { Button } from "../ui/button";
import { DialogRoot, DialogContent, DialogHeader, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { customJoinPageService } from "../../services/customJoinPage.service";
import {
  JOIN_PAGE_REPORT_REASONS,
  type JoinPageReportReason,
} from "../../types/customJoinPage";

interface ReportPageDialogProps {
  open: boolean;
  onClose: () => void;
  username: string;
  brandName: string;
}

export function ReportPageDialog({ open, onClose, username, brandName }: ReportPageDialogProps) {
  const [reason, setReason] = useState<JoinPageReportReason>("SCAM");
  const [details, setDetails] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await customJoinPageService.report(username, {
        reason,
        details: details.trim() || undefined,
        email: email.trim() || undefined,
      });
      toast.success("Thanks — we'll take a look.");
      setDetails("");
      setEmail("");
      onClose();
    } catch {
      toast.error("Couldn't send the report. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogRoot open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent>
        <DialogHeader showCloseButton onClose={onClose}>
          Report this page
        </DialogHeader>
        <p className="mb-4 text-sm text-muted-foreground">
          Tell us what's wrong with {brandName}'s page. Reports go to the Reveshare team.
        </p>
        <div className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">What's the problem?</legend>
            {JOIN_PAGE_REPORT_REASONS.map((r) => (
              <label key={r.value} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="report-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  className="h-4 w-4"
                />
                {r.label}
              </label>
            ))}
          </fieldset>
          <div className="space-y-1.5">
            <Label htmlFor="report-details">Details (optional)</Label>
            <textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={2000}
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="report-email">Your email (optional)</Label>
            <Input
              id="report-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="So we can follow up"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Sending…" : "Send report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
